import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { ShieldAlert } from "lucide-react";
import { getStudentBullyingFlags } from "../../lib/api";

// Format a mailing address for display (mirrors TransportationTab / DemographicsTab).
function formatMailing(s) {
  if (!s) return "";
  const parts = [];
  if (s.mailing_street) parts.push(s.mailing_street);
  const cityLine = [s.mailing_city, s.mailing_state].filter(Boolean).join(", ");
  const cityZip = [cityLine, s.mailing_zip].filter(Boolean).join(" ");
  if (cityZip) parts.push(cityZip);
  if (parts.length) return parts.join(", ");
  return s.mailing_address || "";
}

export function StudentHeader({ 
  student, editing, setEditing, editData, setEditData,
  handleSave, handleDelete, handlePhotoUpload, handlePhotoDelete,
  photoUploading, classGroupConfig, formatDOB, isAdmin, navigate, clinicianName
}) {
  const photoInputRef = useRef(null);
  const [bullyFlag, setBullyFlag] = useState(null);

  useEffect(() => {
    if (!isAdmin || !isAdmin() || !student?.id) { setBullyFlag(null); return; }
    getStudentBullyingFlags(student.id)
      .then((r) => setBullyFlag(r.data?.level ? r.data : null))
      .catch(() => setBullyFlag(null));
  }, [student?.id, isAdmin]);

  const flagLabel = bullyFlag?.level === "investigation" ? "Investigation Open" : "Bullying Warning";
  const flagClass = bullyFlag?.level === "investigation"
    ? "bg-red-100 text-red-800 border border-red-300"
    : "bg-amber-100 text-amber-800 border border-amber-300";
  const flagTitle = (bullyFlag?.details || [])
    .map((d) => `${d.type === "investigation" ? "Investigation" : "Warning"}: ${d.role === "victim" ? "target of" : "listed as aggressor toward"} ${d.other} (${d.count})`)
    .join("\n");

  return (
    <div className="flex items-start justify-between gap-6">
      <div className="flex items-start gap-4 flex-1">
        <Button variant="ghost" onClick={() => navigate("/students")} data-testid="back-btn" className="mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </Button>
        
        {/* Student Preview Card */}
        <div className="flex-1 p-4 rounded-lg border-2 border-[#00305E]/20 bg-gradient-to-r from-[#00305E]/5 to-transparent">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Student Photo */}
              <div className="relative group shrink-0">
                <input type="file" ref={photoInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} data-testid="photo-upload-input" />
                {student.photo ? (
                  <div className="relative">
                    <img src={student.photo} alt={`${student.first_name} ${student.last_name}`} className="w-16 h-16 rounded-full object-cover border-2 border-[#00305E]/30" data-testid="student-photo" />
                    <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      <button onClick={() => photoInputRef.current?.click()} className="p-1 text-white hover:text-blue-200" title="Change photo" data-testid="change-photo-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                      </button>
                      <button onClick={handlePhotoDelete} className="p-1 text-white hover:text-red-300" title="Remove photo" data-testid="remove-photo-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                    className="w-16 h-16 rounded-full bg-[#00305E]/10 border-2 border-dashed border-[#00305E]/30 flex items-center justify-center text-[#00305E]/50 hover:bg-[#00305E]/20 hover:border-[#00305E]/50 transition-colors cursor-pointer"
                    title="Upload photo"
                    data-testid="upload-photo-btn"
                  >
                    {photoUploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00305E]"></div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    )}
                  </button>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-[#00305E] tracking-tight">
                    {student.preferred_name || student.first_name} {student.last_name}
                  </h1>
                  {student.status === 'former' && (
                    <span className="px-2 py-0.5 rounded bg-gray-200 text-gray-600 text-sm font-medium">Former Student</span>
                  )}
                  {bullyFlag && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${flagClass}`} title={flagTitle} data-testid="student-bullying-flag">
                      <ShieldAlert className="w-3.5 h-3.5" />{flagLabel}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {student.student_number && <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded mr-2">{student.student_number}</span>}
                  {student.school_district || "No district assigned"}
                </p>
                <p className="text-sm mt-0.5" data-testid="header-clinician">
                  <span className="text-muted-foreground">Clinician: </span>
                  <span className={clinicianName ? "font-semibold text-[#00305E]" : "text-muted-foreground italic"}>
                    {clinicianName || "Unassigned"}
                  </span>
                </p>
              </div>
            </div>
            <div className="text-right text-sm">
              <span className="px-2 py-1 rounded bg-[#00305E]/10 text-[#00305E] font-medium">
                {student.morning_group ? `AM: ${classGroupConfig.morning_groups?.find(g => g.key === student.morning_group)?.name || `Group ${student.morning_group}`}` : ''} 
                {student.morning_group && student.afternoon_group ? ' \u2022 ' : ''}
                {student.afternoon_group ? `PM: ${classGroupConfig.afternoon_groups?.find(g => g.key === student.afternoon_group)?.name || `Group ${student.afternoon_group}`}` : ''}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#00305E]/10">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date of Birth</p>
              <p className="text-sm font-medium mt-0.5">{formatDOB(student.dob)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Enrollment</p>
              <p className="text-sm font-medium mt-0.5">
                {student.start_date ? formatDOB(student.start_date) : "Not set"}
                {student.end_date && ` - ${formatDOB(student.end_date)}`}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Parent/Guardian</p>
              <p className="text-sm font-medium mt-0.5">{student.first_contact_name || "Not set"}</p>
              {student.first_contact_phone && (
                <p className="text-sm text-muted-foreground">{student.first_contact_phone}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Address</p>
              <p className="text-sm font-medium mt-0.5 line-clamp-2">
                {student.transport_address || formatMailing(student) || student.other_address || "Not set"}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        {isAdmin() && (
          <>
            {editing ? (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); setEditData(student); }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="save-student-btn">
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-student-btn">
                  Edit
                </Button>
                <Button variant="destructive" onClick={handleDelete} data-testid="delete-student-btn">
                  Delete
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
